import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '@/components/ui/Icon';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { toast } from '@/utils/toast';
import { statusService } from '@/services/status.service';
import { SystemService, SystemStatusLevel, UpdateServiceData } from '@/types/status';

/**
 * Service Status Manager
 *
 * Admin interface for managing service status overrides
 */

// Status level visual config
const STATUS_CONFIG: Record<
  SystemStatusLevel,
  { color: string; bgColor: string; label: string }
> = {
  operational: { color: 'text-green-600', bgColor: 'bg-green-100', label: 'Operational' },
  degraded: { color: 'text-amber-600', bgColor: 'bg-amber-100', label: 'Degraded' },
  down: { color: 'text-red-600', bgColor: 'bg-red-100', label: 'Down' },
  maintenance: { color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Maintenance' },
};

const STATUS_OPTIONS = [
  { value: 'none', label: 'None (Use Auto)', icon: 'sync' },
  { value: 'operational', label: 'Operational', icon: 'tick-circle' },
  { value: 'degraded', label: 'Degraded', icon: 'caution' },
  { value: 'down', label: 'Down', icon: 'cross-circle' },
  { value: 'maintenance', label: 'Maintenance', icon: 'tools' },
];

export const ServiceStatusManager = () => {
  const queryClient = useQueryClient();
  const [editingService, setEditingService] = useState<Record<string, { manualStatus: string; statusMessage: string }>>({});

  // Fetch services
  const { data: services, isLoading } = useQuery({
    queryKey: ['admin-services'],
    queryFn: statusService.getAdminServices,
    staleTime: 30 * 1000, // 30 seconds for real-time status
  });

  // Update service mutation
  const updateMutation = useMutation({
    mutationFn: ({ slug, data }: { slug: string; data: UpdateServiceData }) =>
      statusService.updateService(slug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-services'] });
      queryClient.invalidateQueries({ queryKey: ['system-status'] }); // Refresh public status
      toast.success('Service status updated');
      setEditingService({});
    },
    onError: (error: Error) => {
      toast.error(`Failed to update service: ${error.message}`);
    },
  });

  const handleSave = (slug: string) => {
    const edited = editingService[slug];
    if (!edited) return;

    updateMutation.mutate({
      slug,
      data: {
        manualStatus: edited.manualStatus === 'none' ? null : edited.manualStatus as SystemStatusLevel | null,
        statusMessage: edited.statusMessage.trim() || null,
      },
    });
  };

  const handleEdit = (service: SystemService, field: 'manualStatus' | 'statusMessage', value: string) => {
    setEditingService((prev) => ({
      ...prev,
      [service.slug]: {
        manualStatus: field === 'manualStatus' ? value : (prev[service.slug]?.manualStatus ?? service.manualStatus ?? 'none'),
        statusMessage: field === 'statusMessage' ? value : (prev[service.slug]?.statusMessage ?? service.statusMessage ?? ''),
      },
    }));
  };

  const hasChanges = (service: SystemService): boolean => {
    const edited = editingService[service.slug];
    if (!edited) return false;

    return (
      edited.manualStatus !== (service.manualStatus ?? 'none') ||
      edited.statusMessage !== (service.statusMessage ?? '')
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name={ICONS.LOADER} size="lg" color="text-gray-400" ariaHidden />
        <span className="ml-2 text-gray-600">Loading services...</span>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Service Status Overrides</h2>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Service</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Auto Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Manual Override</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Status Message</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {services?.map((service: SystemService) => {
                const autoStatusConfig = STATUS_CONFIG[service.autoStatus as SystemStatusLevel];
                const edited = editingService[service.slug];

                return (
                  <tr key={service.slug} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <span className="font-medium text-gray-900">{service.name}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-1 rounded-md text-xs font-medium',
                          autoStatusConfig.color,
                          autoStatusConfig.bgColor
                        )}
                      >
                        {autoStatusConfig.label}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <Select
                        options={STATUS_OPTIONS}
                        value={edited?.manualStatus ?? service.manualStatus ?? 'none'}
                        onValueChange={(value) => handleEdit(service, 'manualStatus', value)}
                        size="sm"
                        className="w-48"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <Input
                        value={edited?.statusMessage ?? service.statusMessage ?? ''}
                        onChange={(e) => handleEdit(service, 'statusMessage', e.target.value)}
                        placeholder="Optional status message"
                        className="text-sm"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => handleSave(service.slug)}
                        disabled={!hasChanges(service) || updateMutation.isPending}
                        className={cn(
                          'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                          hasChanges(service)
                            ? 'bg-primary-600 text-primary-600-contrast hover:bg-primary-700'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        )}
                      >
                        {updateMutation.isPending ? (
                          <Icon name={ICONS.LOADER} size="sm" color="text-current" ariaHidden />
                        ) : (
                          'Save'
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {(!services || services.length === 0) && (
          <p className="text-center text-gray-500 py-8">No services configured</p>
        )}
      </div>
    </div>
  );
};
